import { useNavigate, useParams } from "react-router-dom";
import useLabels from "../../hooks/useLabels";
import { Button, TankIcon } from "../Generic";
import SecondaryBar from "../SecondaryBar";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { batch, useDispatch, useSelector } from "react-redux";
import {
  selectActiveWinery,
  selectConfigs,
  selectLots,
  selectTanks,
  selectUser,
  selectUsers,
  selectWineLabels,
  selectWineVarieties,
  selectSuppliers,
  selectVineyards,
  selectAnalysis,
  selectOperations,
} from "../../context/selectors";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useEffect, useState, useRef } from "react";
import {
  getLots,
  getTanks,
  getUsers,
  getWineLabels,
  getWineVarieties,
  newLot,
  updateLot,
  getSuppliers,
  getVineyards,
  getProtocols,
} from "../../services/utils";
import { setLots, add, emptyLots, update } from "../../context/lots/lotsSlice";
import {
  DatePicker,
  Input,
  Label,
  MultipleSelect,
  Select,
  Submit,
  Switch,
  TextArea,
  Checkbox,
} from "../FormComponents";
import NewLotCreationForm from "../OperationsForm/NewLotCreationForm";
import { setTanks } from "../../context/tanks/tanksSlice";
import { setWineVarieties } from "../../context/wineVarieties/wineVarietiesSlice";
import { setWineLabels } from "../../context/wineLabels/wineLabelsSlice";
import { setSuppliers } from "../../context/suppliers/suppliersSlice";
import { setVineyards } from "../../context/vineyards/vineyardsSlice";
import { setAnalysis } from "../../context/protocols/analysisSlice";
import {
  ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
  ALPHA_SPACE_APEX_DOT_REGEX,
  INT_REGEX,
  NUM_ALPHA_SPACE_APEX_DOT_REGEX,
  YEAR_REGEX,
} from "../../constants/regex";
import { toast } from "react-toastify";
import { setUsers } from "../../context/users/usersSlice";

import bio from "../../assets/icons/bio.png";
import vegan from "../../assets/icons/vegan.png";
import "./styles/index.scss";
import { toastAxios } from "../../services/toastAxios";

const LotForm = ({
  isOnlyForm = false,
  isDuplicate = false,
  operation = null,
}) => {
  const activeWinery = useSelector(selectActiveWinery);
  const { id } = useParams();
  const [getLabel] = useLabels();
  const navigate = useNavigate();
  const lots = useSelector(selectLots);
  const tanks = useSelector(selectTanks);
  const user = useSelector(selectUser);
  const users = useSelector(selectUsers);
  const configs = useSelector(selectConfigs);
  const wineLabels = useSelector(selectWineLabels);
  const operations = useSelector(selectOperations);
  const wineVarieties = useSelector(selectWineVarieties);
  const suppliers = useSelector(selectSuppliers);
  const vineyards = useSelector(selectVineyards);
  const analysisProtocols = useSelector(selectAnalysis);
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const [isPressed, setIsPressed] = useState(false);
  const [lot, setLot] = useState(
    lots?.length > 0
      ? lots.find((dbLot) => dbLot?.id === Number(id)) || {}
      : {},
  );

  const [isOpen, setIsOpen] = useState(false);
  const [tankList, setTankList] = useState([]);
  const [lotLabelOpen, setLotLabelOpen] = useState(false);
  const [filteredVineyards, setFilteredVineyards] = useState([]);
  const lastAutoPopulatedVineyardRef = useRef(null);
  const isSyncingLabelsRef = useRef(false);
  const syncTimeoutRef = useRef(null);

  // Collapsible sections state
  const [isAnalysisOpen, setIsAnalysisOpen] = useState(false);
  const [isCertificationsOpen, setIsCertificationsOpen] = useState(false);
  const [isHealthStatusOpen, setIsHealthStatusOpen] = useState(false);
  const [isHarvestConditionsOpen, setIsHarvestConditionsOpen] = useState(false);
  const [isDocoOpen, setIsDocoOpen] = useState(false);

  const {
    watch,
    trigger,
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setValue,
    getValues,
    setError,
    clearErrors,
    control,
  } = useForm({
    defaultValues: {
      name: lot?.name?.trim() || "",
      label:
        lot?.label?.map((l) => {
          return {
            value: l || "",
            label: getLabel(`lotLabel_${l}` || ""),
          };
        }) || [],
      varieties: lot?.varieties?.map((v) => {
        return {
          value: v || "",
          label: getLabel(`lotVarieties_${v}` || ""),
        };
      }) || [
        {
          label: "",
          value: "",
        },
      ],
      years: lot?.years || "",
      origins: lot?.origins || "",
      quantity: lot?.actual_quantity || "",
      // quality: {
      //   value: lot?.quality || "MEDIUM",
      //   label: getLabel(`lotQuality_${lot?.quality || "MEDIUM"}`),
      // },
      // state: {
      //   value: lot?.state || "GRAPE",
      //   label: getLabel(`lotState_${lot?.state || "GRAPE"}`),
      // },
      type: lot?.type || "",
      // color: {
      //   value: lot?.color || "RED",
      //   label: getLabel(`lotColor_${lot?.color || "RED"}`),
      // },
      tanks: lot?.tanks || 0,
      dest_tanks: operation?.dest_tanks || [
        {
          tankData: { value: null, label: null },
          quantity: 0,
          new_batch_name: "",
          batch_name_selector: false,
        },
      ],
      supplier_id: lot?.arrival?.supplier_id || null,
      vineyard_id: lot?.arrival?.vineyard_id || null,
      product_line: lot?.product_line || "",
      field_analysis_protocol_id: lot?.field_analysis_protocol_id || null,
      lab_analysis_protocol_id: lot?.lab_analysis_protocol_id || null,
      // lab_analysis_analyst: lot?.lab_analysis_analyst || null,
      lab_analysis_cellarman: [],
      lab_analysis_priority: null,
      lab_analysis_custom_name: null,
      lab_analysis_expire_date: null,
      lab_analysis_expire_date_exact: false,
      field_analysis_values: [],
      certifications: lot?.certifications || {
        organic: false,
        vegan: false,
        biodynamic: false,
        documents: [],
        iso_traceability: {
          field_lot: "",
          vineyard_registry: "",
          phytosanitary_treatments: "",
        },
      },
      state: lot?.state || "GRAPE",
      health_status: lot?.health_status || {
        condition: "",
        botrytis_percent: "",
        rot_percent: "",
        damaged_berries_percent: "",
        leaves_stems_percent: "",
        immature_berries_percent: "",
        notes: "",
      },
      harvest_conditions: lot?.harvest_conditions || {
        harvest_date: new Date().toISOString().split("T")[0],
        method: "",
        container_type: "",
        weather: {
          harvest_temperature: "",
          humidity: "",
          conditions: "",
          days_since_last_rain: "",
        },
        transport: {
          time_from_harvest_hours: "",
          transport_temperature: "",
          notes: "",
        },
      },
    },
    mode: "onTouched",
  });

  const {
    color,
    label,
    varieties,
    certifications: watchedCertifications,
    state: watchedState,
    field_analysis_protocol_id: watchedFieldProtocolId,
    lab_analysis_protocol_id: watchedLabProtocolId,
  } = useWatch({ control });

  const {
    fields: varietiesFields,
    append: appendV,
    remove: removeV,
    replace: replaceV,
  } = useFieldArray({
    control,
    name: "varieties",
  });

  const {
    fields: documentsFields,
    append: appendDoc,
    remove: removeDoc,
  } = useFieldArray({
    control,
    name: "certifications.documents",
  });

  useEffect(() => {
    if (!lots || lots.length === 0) {
      loadLots();
    }
    if (!tanks || tanks.length === 0) {
      loadTanks();
    }

    if (!users || users.length === 0) {
      loadUsers();
    }
    if (!wineLabels || wineLabels.length === 0) {
      loadWineLabels();
    }
    if (!wineVarieties || wineVarieties.length === 0) {
      loadWineVarieties();
    }
    if (!suppliers || suppliers.length === 0) {
      loadSuppliers();
    }
    if (!vineyards || vineyards.length === 0) {
      loadVineyards();
    }
    if (!analysisProtocols || analysisProtocols.length === 0) {
      loadAnalysisProtocols();
    }
  }, []);

  // Watch for supplier and vineyard changes
  const { supplier_id, vineyard_id } = useWatch({ control });

  // Filter vineyards by selected supplier
  useEffect(() => {
    if (supplier_id?.value) {
      const filtered =
        vineyards?.filter((v) => v.supplier_id === supplier_id.value) || [];
      setFilteredVineyards(filtered);
    } else {
      setFilteredVineyards([]);
    }
  }, [supplier_id, vineyards]);

  // Auto-populate varieties when vineyard is selected
  useEffect(() => {
    if (
      vineyard_id?.value &&
      lastAutoPopulatedVineyardRef.current !== vineyard_id.value
    ) {
      const selectedVineyard = vineyards.find(
        (v) => v.id === vineyard_id.value,
      );
      if (selectedVineyard?.main_varieties?.length > 0) {
        const numVarieties = selectedVineyard.main_varieties.length;
        const equalPercentage = Math.floor(100 / numVarieties);
        const remainder = 100 - equalPercentage * numVarieties;

        // Set varieties
        setValue(
          "varieties",
          selectedVineyard.main_varieties.map((v, i) => ({
            value: v,
            label: getLabel(`lotVarieties_${v}`),
          })),
        );

        // Set percentages (distribute evenly, add remainder to first)
        const percentages = selectedVineyard.main_varieties.map((v, i) =>
          i === 0 ? equalPercentage + remainder : equalPercentage,
        );
        setValue("percentages", percentages);

        // Trigger validation
        trigger(["varieties", "percentages"]);

        // Mark this vineyard as auto-populated
        lastAutoPopulatedVineyardRef.current = vineyard_id.value;
      }
    } else if (!vineyard_id?.value) {
      // Reset ref when vineyard is cleared
      lastAutoPopulatedVineyardRef.current = null;
    }
  }, [vineyard_id, vineyards]);

  // Sync certifications with label field (Bio/Vegan/Biodynamic)
  useEffect(() => {
    if (isSyncingLabelsRef.current || !label) return;

    // Clear any pending timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Debounce the sync to avoid rapid updates
    syncTimeoutRef.current = setTimeout(() => {
      const currentLabels = label || [];
      const hasBio = currentLabels.some((l) => l?.value === "Bio");
      const hasVegan = currentLabels.some((l) => l?.value === "Vegan");
      const hasBiodynamic = currentLabels.some(
        (l) => l?.value === "Biodynamic",
      );

      // Get current values to avoid unnecessary updates
      const currentCerts = getValues("certifications");

      isSyncingLabelsRef.current = true;

      if (hasBio !== currentCerts?.organic) {
        setValue("certifications.organic", hasBio, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }

      if (hasVegan !== currentCerts?.vegan) {
        setValue("certifications.vegan", hasVegan, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }

      if (hasBiodynamic !== currentCerts?.biodynamic) {
        setValue("certifications.biodynamic", hasBiodynamic, {
          shouldValidate: false,
          shouldDirty: true,
        });
      }

      setTimeout(() => {
        isSyncingLabelsRef.current = false;
      }, 100);
    }, 150);
  }, [label]);

  // Sync label field with certifications (Bio/Vegan/Biodynamic)
  useEffect(() => {
    if (isSyncingLabelsRef.current || !watchedCertifications) return;

    // Clear any pending timeout
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
    }

    // Debounce the sync to avoid rapid updates
    syncTimeoutRef.current = setTimeout(() => {
      const currentLabels = getValues("label") || [];
      const hasBio = currentLabels.some((l) => l?.value === "Bio");
      const hasVegan = currentLabels.some((l) => l?.value === "Vegan");
      const hasBiodynamic = currentLabels.some(
        (l) => l?.value === "Biodynamic",
      );

      let updatedLabels = [...currentLabels];
      let needsUpdate = false;

      // Handle Bio (Organic)
      if (watchedCertifications?.organic && !hasBio) {
        updatedLabels.push({
          value: "Bio",
          label: getLabel("lotLabel_Bio"),
          type: "label",
        });
        needsUpdate = true;
      } else if (!watchedCertifications?.organic && hasBio) {
        updatedLabels = updatedLabels.filter((l) => l?.value !== "Bio");
        needsUpdate = true;
      }

      // Handle Vegan
      if (watchedCertifications?.vegan && !hasVegan) {
        updatedLabels.push({
          value: "Vegan",
          label: getLabel("lotLabel_Vegan"),
          type: "label",
        });
        needsUpdate = true;
      } else if (!watchedCertifications?.vegan && hasVegan) {
        updatedLabels = updatedLabels.filter((l) => l?.value !== "Vegan");
        needsUpdate = true;
      }

      // Handle Biodynamic
      if (watchedCertifications?.biodynamic && !hasBiodynamic) {
        updatedLabels.push({
          value: "Biodynamic",
          label: getLabel("lotLabel_Biodynamic"),
          type: "label",
        });
        needsUpdate = true;
      } else if (!watchedCertifications?.biodynamic && hasBiodynamic) {
        updatedLabels = updatedLabels.filter((l) => l?.value !== "Biodynamic");
        needsUpdate = true;
      }

      if (needsUpdate) {
        isSyncingLabelsRef.current = true;
        setValue("label", updatedLabels, {
          shouldValidate: false,
          shouldDirty: true,
        });
        setTimeout(() => {
          isSyncingLabelsRef.current = false;
        }, 100);
      }
    }, 150);
  }, [
    watchedCertifications?.organic,
    watchedCertifications?.vegan,
    watchedCertifications?.biodynamic,
  ]);

  useEffect(() => {
    const filteredSet = [];
    lot?.tanks?.forEach((tank) => {
      const t = tanks.find((t) => t.id === tank.tank_id);
      filteredSet.push(getTankData(t));
    });

    setTankList(filteredSet);
  }, [lot]);

  const getTankData = (tank) => {
    const tankData = {
      ...tank,
      planned_quantity: tank?.is_planned ? tank?.planned_quantity || 0 : 0,
    };
    // TO DO check with planned ops

    return tankData;
  };

  useEffect(() => {
    const singleLot =
      id && lots.length > 0 && !isOnlyForm
        ? lots.find((dbLot) => dbLot?.id === Number(id)) || null
        : isOnlyForm && operation !== null
          ? operation?.src_tanks?.[0] || null
          : null;
    // TO DO ho tolto Object.keys(lot).length === 0 ma non so se funziona ovunque
    // const singleLot = (id && lots.length > 0 && Object.keys(lot).length === 0 && !isOnlyForm) ?
    //   (lots.find((dbLot) => dbLot.id === Number(id)) || null) :
    //   (isOnlyForm && operation !== null) ?
    //   (operation?.src_tanks?.[0] || null) : null;

    if (singleLot) {
      setLot(singleLot);
      setValue("quality", {
        value: singleLot?.quality || "LOW",
        label: getLabel(`lotQuality_${singleLot?.quality || "LOW"}`),
      });
      if (singleLot?.product_line) {
        setValue("product_line", {
          value: singleLot?.product_line,
          label: getLabel(`lotProductLine_${singleLot?.product_line}`),
        });
      }
      if (singleLot?.field_analysis_protocol_id) {
        const protocol = analysisProtocols?.find(
          (p) => p.id === singleLot.field_analysis_protocol_id,
        );
        setValue("field_analysis_protocol_id", {
          value: singleLot.field_analysis_protocol_id,
          label: protocol?.protocols_name,
        });
        // Popola i valori se esistono
        if (singleLot?.field_analysis?.length && protocol?.analysis) {
          singleLot.field_analysis.forEach((item) => {
            const idx = protocol.analysis.findIndex(
              (a) => a.substance_id === item.analysis_id,
            );
            if (idx !== -1) {
              setValue(`field_analysis_values.${idx}`, item.value);
            }
          });
        }
      }
      if (singleLot?.lab_analysis_protocol_id) {
        const protocol = analysisProtocols?.find(
          (p) => p.id === singleLot.lab_analysis_protocol_id,
        );
        setValue("lab_analysis_protocol_id", {
          value: singleLot.lab_analysis_protocol_id,
          label:
            protocol?.protocols_name ||
            `Protocol ${singleLot.lab_analysis_protocol_id}`,
        });

        // Load lab analysis operation fields if they exist
        // if (singleLot?.lab_analysis_analyst) {
        //   setValue("lab_analysis_analyst", singleLot.lab_analysis_analyst);
        // }
        if (singleLot?.lab_analysis_cellarman) {
          setValue("lab_analysis_cellarman", singleLot.lab_analysis_cellarman);
        }
        if (singleLot?.lab_analysis_custom_name) {
          setValue("lab_analysis_custom_name", singleLot.lab_analysis_custom_name);
        }
        if (singleLot?.lab_analysis_priority != null) {
          setValue("lab_analysis_priority", {
            value: String(singleLot.lab_analysis_priority),
            label: getLabel(`lotQuality_${singleLot.lab_analysis_priority === 2 ? 'HIGH' : singleLot.lab_analysis_priority === 1 ? 'MEDIUM' : 'LOW'}`),
          });
        }
        if (singleLot?.lab_analysis_expire_date) {
          setValue("lab_analysis_expire_date", new Date(singleLot.lab_analysis_expire_date));
        }
        if (singleLot?.lab_analysis_expire_date_exact != null) {
          setValue("lab_analysis_expire_date_exact", singleLot.lab_analysis_expire_date_exact);
        }
      }
      if (singleLot?.certifications) {
        setValue("certifications", singleLot.certifications);
      }
      if (singleLot?.health_status) {
        setValue("health_status", singleLot.health_status);
        if (singleLot.health_status.condition) {
          setValue("health_status.condition", {
            value: singleLot.health_status.condition,
            label: getLabel(
              `lotHealthCondition_${singleLot.health_status.condition}`,
            ),
          });
        }
      }
      if (singleLot?.harvest_conditions) {
        setValue("harvest_conditions", singleLot.harvest_conditions);
        if (singleLot.harvest_conditions.method) {
          setValue("harvest_conditions.method", {
            value: singleLot.harvest_conditions.method,
            label: getLabel(
              `lotHarvestMethod_${singleLot.harvest_conditions.method}`,
            ),
          });
        }
      }
      // const varietyList = [];
      // singleLot?.varieties?.forEach((element, i) => {
      //   varietyList.push({
      //     value: element || "",
      //     label: getLabel(`lotVarieties_${element}` || ""),
      //     quantity: singleLot?.percentages?.[i],
      //   });
      // });

      // setValue("varieties", varietyList);
      // if (!isOnlyForm) {
      //   singleLot?.years?.forEach((element, i) => {
      //     setValue(`years.${i}`, singleLot?.years[i] || "");
      //   });
      //   singleLot?.origins?.forEach((element, i) => {
      //     setValue(`origins.${i}`, singleLot?.origins[i] || "");
      //   });
      // } else {
      //   setValue("years", singleLot?.years[0] || "");
      //   setValue("origins", singleLot?.origins[0] || "");
      // }
      if (singleLot?.composition?.length) {
        const varietyList = singleLot.composition.map((item) => ({
          value: item.variety || "",
          label: getLabel(`lotVarieties_${item.variety}` || ""),
          quantity: item.percentage || null,
        }));

        setValue("varieties", varietyList);
        setValue("years", singleLot.composition[0]?.year || "");
        setValue("origins", singleLot.composition[0]?.origin || "");
      }
      setValue("state", {
        value: singleLot?.state,
        label: getLabel(`lotState_${singleLot?.state || "GRAPE"}`),
      });

      setValue(
        "quantity",
        (isOnlyForm
          ? singleLot?.initial_quantity
          : singleLot?.actual_quantity) || 0,
      );
      const labelList = [];
      singleLot?.label?.forEach((element) => {
        labelList.push({
          value: element || "",
          label: getLabel(`lotLabel_${element || ""}`),
          type: ["Bio", "Vegan", "Biodynamic"].includes(element)
            ? "label"
            : "wine",
        });
      });
      setValue("label", labelList);

      // Sync certifications with label Bio/Vegan/Biodynamic on initial load
      const hasBio = labelList.some((l) => l?.value === "Bio");
      const hasVegan = labelList.some((l) => l?.value === "Vegan");
      const hasBiodynamic = labelList.some((l) => l?.value === "Biodynamic");
      if (singleLot?.certifications) {
        setValue(
          "certifications.organic",
          hasBio || singleLot.certifications.organic,
        );
        setValue(
          "certifications.vegan",
          hasVegan || singleLot.certifications.vegan,
        );
        setValue(
          "certifications.biodynamic",
          hasBiodynamic || singleLot.certifications.biodynamic,
        );
      } else {
        setValue("certifications.organic", hasBio);
        setValue("certifications.vegan", hasVegan);
        setValue("certifications.biodynamic", hasBiodynamic);
      }
      setValue("type", singleLot?.type || "");
      setValue("tanks", singleLot?.tanks || "");
      setValue(
        "name",
        `${singleLot?.name || ""} ${isDuplicate ? getLabel("copy") : ""}`?.trim(),
      );

      setValue("color", {
        value: singleLot?.color || "RED",
        label: getLabel(`lotColor_${singleLot?.color || "RED"}`),
      });

      setValue("note", (isOnlyForm ? operation?.note : singleLot?.note) || "");

      const cellarmans = [];
      operation?.cellarman_ids?.map((u) => {
        const userT = (
          user.role === 2
            ? users?.filter(
                (u) => u.username != user.userName && u.role_id === 3,
              )
            : users
        )?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: userT?.username,
        });
      });
      setValue("cellarman_ids", cellarmans);
      setValue("priority", {
        label: getLabel(`operationPriority${operation?.priority}`),
        value: operation?.priority,
      });
      setValue("expire_date", operation?.expire_date);
    }
  }, [lots, operation]);

  useEffect(() => {
    if (label?.length === 0) {
      setLotLabelOpen(false);
      return;
    } else
      setLotLabelOpen(label?.find((l) => l?.value && l?.type === undefined));
  }, [label]);

  const testLabel = (limits) => {
    let status = "OK";
    if (!label || label?.length === 0) {
      return status;
    }

    // in case of same varieties (different origin/year), sum the quantities
    const summedVarieties = Object.values(
      varieties?.reduce((acc, curr) => {
        const key = curr.label;
        if (!acc[key]) {
          acc[key] = { ...curr };
        } else {
          acc[key].quantity += curr.quantity;
        }
        return acc;
      }, {}),
    );

    limits?.forEach((limit) => {
      switch (limit?.type) {
        case "RANGE":
          summedVarieties?.forEach((variety) => {
            if (!variety?.quantity || !limit?.wines?.includes(variety?.label))
              return true;
            const quantity = Number(variety?.quantity) || 0;
            if (limit?.min && quantity < limit?.min * 100)
              status = getLabel("toast_lotVarietiesStatus_MIN", {
                variety: variety?.label,
                quantity: quantity,
                value: limit?.min * 100,
              });
            else if (limit?.max && quantity > limit?.max * 100)
              status = getLabel("toast_lotVarietiesStatus_MAX", {
                variety: variety?.label,
                quantity: quantity,
                value: limit?.max * 100,
              });
          });
          break;
        case "SUM":
          const total = summedVarieties
            ?.filter((v) => limit?.wines?.some((l) => v?.label === l))
            ?.reduce((acc, curr) => {
              return acc + (Number(curr?.quantity) || 0);
            }, 0);
          if (limit?.min && total < limit?.min * 100)
            status = getLabel("toast_lotVarietiesStatus_SUM_MIN", {
              varieties: limit?.wines,
              value: limit?.min * 100,
            });
          if (limit?.max && total > limit?.max * 100)
            status = getLabel("toast_lotVarietiesStatus_SUM_MAX", {
              varieties: limit?.wines,
              value: limit?.max * 100,
            });
          break;
        case "COLOR":
          const color = limit?.color;
          if (color in wineVarieties) {
            const varietiesByColor = wineVarieties[color];
            const totalColor = summedVarieties
              ?.filter((v) => varietiesByColor?.some((l) => v?.label === l))
              ?.reduce((acc, curr) => {
                return acc + (Number(curr?.quantity) || 0);
              }, 0);

            if (limit?.min && totalColor < limit?.min * 100)
              status = getLabel("toast_lotVarietiesStatus_COLOR_MIN", {
                color: getLabel(`lotColor_${label?.color}`),
                varieties: limit?.wines,
                value: limit?.min * 100,
              });
            if (limit?.max && totalColor > limit?.max * 100)
              status = getLabel("toast_lotVarietiesStatus_COLOR_MAX", {
                color: getLabel(`lotColor_${label?.color}`),
                varieties: limit?.wines,
                value: limit?.max * 100,
              });
          }
          break;
        default:
          break;
      }
    });
    return status;
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const getTotalQuantity = () => {
    return (
      getValues()?.dest_tanks?.reduce((acc, curr) => {
        return acc + (Number(curr?.quantity) || 0);
      }, 0) || 0
    );
  };

  const customIdInvalidQuantities = "custom-id-invalid-quantities";
  const openFormModal = () => {
    // console.log('errors', errors)
    // if (Object.keys(errors)?.length > 0) return;

    // trigger();
    if (
      getValues()?.dest_tanks?.filter((t) => t?.tankData?.value)?.length === 0
    ) {
      toast(getLabel("toast_destemmingMissingDests"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return false;
    } else {
      toast.dismiss(customIdMissingDests);
    }

    if (
      getValues()?.dest_tanks?.filter(
        (t) =>
          (!t?.batch_name_selector?.value &&
            t?.batch_name_selector?.label?.length === 0) ||
          (t?.batch_name_selector?.value === true &&
            t?.new_batch_name?.length === 0),
      )?.length > 0
    ) {
      toast(getLabel("toast_destemmingMissingLotSelectors"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return false;
    } else {
      toast.dismiss(customIdMissingDests);
    }

    const creationLiters = getTotalQuantity();
    const totalLitersAvailable = Number(getValues("quantity"));
    if (creationLiters === totalLitersAvailable) {
      toast.dismiss(customIdInvalidQuantities);
      // console.log('checkVarietiesErrors', checkVarietiesErrors())
      if (checkVarietiesErrors()) {
        setIsOpen(true);
        return true;
      } else return false;
      // return true;
    } else {
      toast(
        getLabel("toast_lotCreationInvalidQuantities", {
          sum: creationLiters || "0",
          total: totalLitersAvailable || "0",
          state: getLabel(
            `lotState_${getValues("state")?.value}`,
          )?.toLowerCase(),
        }),
        {
          toastId: customIdInvalidQuantities,
          type: toast.TYPE.ERROR,
          isLoading: false,
          position: toast.POSITION.BOTTOM_RIGHT,
        },
      );
      return false;
    }
  };

  const customId = "custom-id-invalid-label";
  const customIdQuantity = "custom-id-invalid-quantity";
  const customIdPerc = "custom-id-invalid-percentage";
  const customIdMissingDests = "custom-id-missing-dests";

  const checkVarietiesErrors = () => {
    if (!label || label?.length === 0) {
      clearErrors("label");
      toast.dismiss(customIdPerc);
      // return false;
    }
    const total =
      varieties?.length === 1
        ? 100
        : varieties?.reduce((acc, curr) => {
            return acc + (Number(curr?.quantity) || 0);
          }, 0);
    if (total !== 100) {
      varieties?.forEach((v, index) =>
        setError(`varieties.${index}.quantity`, {
          type: "manual",
          message: getLabel("errorWinePercentage100"),
        }),
      );
      return false;
    } else {
      varieties?.forEach((v, index) =>
        clearErrors(`varieties.${index}.quantity`),
      );
    }
    if (errors?.length > 0) return;

    if (!varieties || varieties?.length === 0) return false;
    const wLabel = label?.find((l) => l?.type === undefined);
    const wineLabel =
      wineLabels?.wine?.length > 0
        ? wineLabels?.wine?.find((l) => l.id === wLabel?.value)
        : null;

    let isValid = true;
    const labelStatus = testLabel(wineLabel?.limits || []);

    if (labelStatus !== "OK") {
      toast.warning(labelStatus, {
        toastId: customIdPerc,
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
        autoClose: false,
      });
    } else toast.dismiss(customIdPerc);

    // if (testLabel(wineLabel?.limits || [])) {
    //   toast.warning(getLabel("toast_lotVarietiesPercentage"), {
    //     toastId: customIdPerc,
    //     position: toast.POSITION.BOTTOM_RIGHT,
    //     exclude: true,
    //     autoClose: false
    //   });
    // } else {
    //   toast.dismiss(customIdPerc);
    // }
    varieties?.forEach((variety) => {
      if (
        !variety?.label ||
        !wineLabel?.limits ||
        wineLabel?.limits?.length === 0
      )
        return false;
      isValid = wineLabel?.limits?.some((wine) =>
        wine?.wines?.includes(variety?.label),
      );
      if (!isValid) {
        toast.warning(getLabel("toast_lotVarietiesNotInLabel"), {
          toastId: customId,
          position: toast.POSITION.BOTTOM_RIGHT,
          exclude: true,
        });
        setError("label", {
          type: "manual",
          message: getLabel("errorWineLabel"),
        });
        return false;
      }
    });

    if (isValid) {
      toast.dismiss(customId);
      clearErrors("label");
      return true;
    }
  };

  useEffect(() => {
    checkVarietiesErrors();
  }, [label, varieties]);

  const loadLots = async () => {
    const currentLots = await getLots(activeWinery.id, axiosPrivate);
    dispatch(setLots(currentLots));
  };

  const loadTanks = async () => {
    const currentTanks = await getTanks(activeWinery.id, axiosPrivate);
    dispatch(setTanks(currentTanks));
  };

  const loadUsers = async () => {
    const currentUsers = await getUsers(axiosPrivate);
    dispatch(setUsers(currentUsers));
  };

  const loadWineLabels = async () => {
    const currentWineLabels = await getWineLabels(axiosPrivate);
    dispatch(setWineLabels(currentWineLabels));
  };

  const loadWineVarieties = async () => {
    const currentWineVarieties = await getWineVarieties(axiosPrivate);
    dispatch(setWineVarieties(currentWineVarieties));
  };

  const loadSuppliers = async () => {
    const currentSuppliers = await getSuppliers(activeWinery.id, axiosPrivate);
    dispatch(setSuppliers(currentSuppliers));
  };

  const loadVineyards = async () => {
    const currentVineyards = await getVineyards(activeWinery.id, axiosPrivate);
    dispatch(setVineyards(currentVineyards));
  };

  const loadAnalysisProtocols = async () => {
    const currentProtocols = await getProtocols("ANALYSIS", axiosPrivate);
    dispatch(setAnalysis(currentProtocols));
  };

  const getError = (idx, data) => {
    if (
      !errors ||
      Object.keys(errors)?.length === 0 ||
      !data ||
      data?.length <= idx
    )
      return;

    return data[idx]?.quantity || null;
  };

  const customIdNewLot = "custom-id-new-lot";
  const customIdUpdateLot = "custom-id-update-lot";

  const submitForm = async (data) => {
    console.log("errors", errors);
    if (Object.keys(errors)?.length > 0) return;
    const newLotCreationStatus =
      (!id || (id && isOnlyForm)) &&
      Number(data?.quantity) > 0 &&
      ["WINE", "MUST"].includes(data?.state?.value);
    if (newLotCreationStatus) {
      openFormModal();
      return;
    }

    const total =
      varieties?.length === 1
        ? 100
        : varieties?.reduce((acc, curr) => {
            return acc + (Number(curr?.quantity) || 0);
          }, 0);
    if (total !== 100) {
      varieties?.forEach((v, index) =>
        setError(`varieties.${index}.quantity`, {
          type: "manual",
          message: getLabel("errorWinePercentage100"),
        }),
      );
    } else {
      varieties?.forEach((v, index) =>
        clearErrors(`varieties.${index}.quantity`),
      );
    }
    if (errors?.length > 0) return;

    if (!id && lots?.find((l) => l?.name === data?.name?.trim())) {
      toast.error(getLabel("toast_lotNameNotAvailable"), {
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
      });
      return;
    }
    const percentages =
      varieties?.length > 1
        ? varieties?.map((v) => Number(v?.quantity))
        : [100];
    const health_status = data?.health_status
      ? {
          ...data?.health_status,
          condition: data.health_status?.condition?.value ?? null,
        }
      : null;
    const harvest_conditions = data?.harvest_conditions
      ? {
          ...data?.harvest_conditions,
          method: data.harvest_conditions?.method?.value ?? null,
        }
      : null;
    const tmpDate = new Date(data?.lab_analysis_expire_date)?.setHours(
      23,
      59,
      59,
    );

    const requestPayload = {
      ...data,
      percentages: percentages,
      varieties: data?.varieties?.map((v) => v?.label),
      years: Array.isArray(data?.years)
        ? data?.years
        : data?.varieties?.map(() => data?.years),
      origins: Array.isArray(data?.origins)
        ? data?.origins
        : data?.varieties?.map(() => data?.origins),
      state: data?.state?.value || "GRAPE",
      unit_id: 4,
      label: data?.label?.map((v) => v?.value),
      color: data?.color?.value || "RED",
      quality: data?.quality?.value || "LOW",
      product_line: data?.product_line?.value || null,
      field_analysis_protocol_id:
        data?.field_analysis_protocol_id?.value || null,
      lab_analysis_protocol_id: data?.lab_analysis_protocol_id?.value || null,
      // lab_analysis_analyst: data?.lab_analysis_analyst || null,
      ...(data?.lab_analysis_protocol_id?.value && {
        is_exact_date: data?.lab_analysis_expire_date_exact || false,
        expire_date: new Date(tmpDate)?.toISOString(),
        priority: data?.lab_analysis_priority?.value ? parseInt(data.lab_analysis_priority.value) : null,
        winery_id: activeWinery?.id,
        custom_name: data?.lab_analysis_custom_name,
        cellarman_ids: data?.lab_analysis_cellarman?.map(
          (cellMan) => cellMan?.value,
        ),
      }),
      field_analysis: (() => {
        if (!data?.field_analysis_protocol_id?.value) return null;
        const protocol = analysisProtocols?.find(
          (p) => p.protocols_id === data.field_analysis_protocol_id.value,
        );
        if (!protocol?.analysis?.length) return null;
        return protocol.analysis
          .map((analysisItem, idx) => {
            const value = data.field_analysis_values?.[idx];
            if (value === undefined || value === null || value === "")
              return null;
            return {
              analysis_id: analysisItem.substance_id,
              value: Number(value),
            };
          })
          .filter((item) => item !== null);
      })(),
      certifications: data?.certifications || null,
      health_status: health_status,
      harvest_conditions: harvest_conditions,
      winery_id: activeWinery.id,
      ...(id && { id: id }),
      supplier_id: data?.supplier_id?.value || null,
      vineyard_id: data?.vineyard_id?.value || null,
    };
    console.log("requestPayload", requestPayload);
    // return;

    let response = null;
    if (id && !isDuplicate) {
      response = await toastAxios(
        () => updateLot(id, requestPayload, axiosPrivate),
        customIdUpdateLot,
        { name: data?.name || "" },
        getLabel,
      );
    } else {
      response = await toastAxios(
        () => newLot(requestPayload, axiosPrivate),
        customIdNewLot,
        { name: data?.name || "" },
        getLabel,
      );
    }

    console.log("response", response);

    if (response && response?.success) {
      const updated_data = response?.updated_data || null;
      // console.log('updated_data', updated_data)
      updateStore(updated_data);
      navClickHandler();
    }

    toast.dismiss(customIdPerc);
  };

  const updateStore = (payload) => {
    if (id && !isDuplicate) {
      dispatch(update(payload));
    } else {
      dispatch(add(payload));
    }
  };

  const navClickHandler = () => {
    if (id && !isDuplicate) {
      navigate(`/lots/${id}`);
    } else {
      navigate("/lots");
    }
  };

  const getWineVarietiesOptionsByColor = () => {
    if (!getValues("color")?.value) return [];
    const color = getValues("color")?.value?.toLowerCase();

    return color in wineVarieties
      ? wineVarieties[color]?.filter(
          (v) => !varieties?.some?.((variety) => variety?.value == v),
        )
      : [];
  };

  const getWineLabelsOptions = () => {
    if (!wineLabels || wineLabels?.length === 0) return [];
    const labels = getValues("label");
    if (labels && labels?.filter((l) => l?.type !== "label")?.length > 0)
      return wineLabels?.labels?.map((l) => {
        return { value: l?.id, label: l?.id, type: l?.type };
      });
    return (
      [...wineLabels?.wine, ...wineLabels?.labels]?.map((l) => {
        return { value: l?.id, label: l?.id, type: l?.type };
      }) || []
    );
  };

  // Filtra opzioni protocolli di analisi per mutua esclusione
  const getFieldAnalysisOptions = () => {
    const labProtocolId = watchedLabProtocolId?.value;
    return (
      analysisProtocols
        ?.filter(
          (p) =>
            p.state === "GRAPE" &&
            (!labProtocolId || p.protocols_id !== labProtocolId),
        )
        ?.map((p) => ({
          value: p.protocols_id,
          label: p.protocols_name,
        })) || []
    );
  };

  const getLabAnalysisOptions = () => {
    const fieldProtocolId = watchedFieldProtocolId?.value;
    return (
      analysisProtocols
        ?.filter(
          (p) =>
            p.state === "GRAPE" &&
            (!fieldProtocolId || p.protocols_id !== fieldProtocolId),
        )
        ?.map((p) => ({
          value: p.protocols_id,
          label: p.protocols_name,
        })) || []
    );
  };

  const selectedFieldProtocol = watchedFieldProtocolId?.value
    ? analysisProtocols?.find((p) => {
        return p.protocols_id === watchedFieldProtocolId.value;
      })
    : null;

  const selectedLabProtocol = watchedLabProtocolId?.value
    ? analysisProtocols?.find(
        (p) => p.protocols_id === watchedLabProtocolId.value,
      )
    : null;

  const renderNavMenu = () => {
    const navClickHandler = () => {
      if (id) {
        navigate(`/lots/${id}`);
      } else {
        navigate("/lots");
      }
    };
    return (
      <div className="primary_container_menu">
        <div className="primary_container_row">
          <Button arrowDirection="left" onClick={navClickHandler} />
          <h2>
            {getLabel(
              id && !isDuplicate
                ? "lotsUpdatePage"
                : isDuplicate
                  ? "lotsCopyPage"
                  : "lotsNewPage",
            )}
          </h2>
        </div>
      </div>
    );
  };

  const renderForm = () => {
    return (
      <div className="lot_form_wrapper">
        <div className="lot_form">
          <form
            onSubmit={handleSubmit(submitForm)}
            autoComplete="off"
            noValidate
          >
            <div className="lot_form_wrapper_row">
              <Input
                name="name"
                register={register}
                type="text"
                label={getLabel("lotName")}
                placeholder={getLabel("lotNamePlaceholder")}
                required={getLabel("inputRequiredError")}
                minLength={{
                  value: 3,
                  message: getLabel("errorMinLength", { value: 3 }),
                }}
                maxLength={{
                  value: 24,
                  message: getLabel("errorMaxLength", { value: 24 }),
                }}
                regExpPattern={{
                  value: ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
                  message: getLabel("errorAlphaNumSpace_-"),
                }}
                error={errors["name"]}
                tooltip={getLabel("lotNameTooltip")}
              />
              <MultipleSelect
                name="label"
                control={control}
                label={getLabel("lotLabel")}
                placeholder={getLabel("lotLabelPlaceholder")}
                error={errors["label"]}
                // required={getLabel("inputRequiredError")}
                options={getWineLabelsOptions()}
                tooltip={getLabel("lotLabelTooltip")}
              />
            </div>
            <div className="lot_form_wrapper_row">
              {(!id || isOnlyForm || isDuplicate) && (
                <>
                  <Select
                    name="state"
                    control={control}
                    label={getLabel("lotState")}
                    placeholder={getLabel("lotStatePlaceholder")}
                    error={errors["state"]}
                    required={getLabel("inputRequiredError")}
                    isDisabled={
                      id && !isDuplicate && !isOnlyForm ? true : false
                    }
                    options={[
                      {
                        value: "GRAPE",
                        label: getLabel("lotState_GRAPE"),
                      },
                      {
                        value: "MUST",
                        label: getLabel("lotState_MUST"),
                      },
                      {
                        value: "WINE",
                        label: getLabel("lotState_WINE"),
                      },
                    ]}
                    tooltip={getLabel("lotStateTooltip")}
                  />
                  <Select
                    name="color"
                    control={control}
                    label={getLabel("lotColor")}
                    placeholder={getLabel("lotColorPlaceholder")}
                    error={errors["color"]}
                    required={getLabel("inputRequiredError")}
                    isDisabled={
                      id && !isDuplicate && !isOnlyForm ? true : false
                    }
                    onTableChange={(newColor) => {
                      // Filter varieties to keep only those compatible with the new color
                      const currentVarieties = getValues("varieties") || [];
                      const newColorLower = newColor?.value?.toLowerCase();

                      if (
                        newColorLower &&
                        wineVarieties &&
                        newColorLower in wineVarieties
                      ) {
                        const allowedVarieties = wineVarieties[newColorLower];
                        const filteredVarieties = currentVarieties.filter(
                          (v) => v?.value && allowedVarieties.includes(v.value),
                        );

                        // If all varieties are filtered out, reset to one empty field
                        if (filteredVarieties.length === 0) {
                          replaceV([
                            {
                              value: null,
                              label: null,
                            },
                          ]);
                        } else {
                          // Keep compatible varieties
                          replaceV(filteredVarieties);
                        }
                      }
                    }}
                    options={[
                      {
                        value: "WHITE",
                        label: getLabel("lotColor_WHITE"),
                      },
                      {
                        value: "RED",
                        label: getLabel("lotColor_RED"),
                      },
                      ...(getValues("state")?.value &&
                      getValues("state")?.value !== "GRAPE"
                        ? [{ value: "ROSE", label: getLabel("lotColor_ROSE") }]
                        : []),
                    ]}
                    // defaultValue={{
                    //   value: "RED",
                    //   label: getLabel("lotColor_RED"),
                    // }}
                    tooltip={getLabel("lotColorTooltip")}
                  />
                </>
              )}
              <Select
                name="quality"
                control={control}
                label={getLabel("lotQuality")}
                placeholder={getLabel("lotQualityPlaceholder")}
                error={errors["quality"]}
                required={getLabel("inputRequiredError")}
                options={[
                  {
                    value: "LOW",
                    label: getLabel("lotQuality_LOW"),
                  },
                  {
                    value: "MEDIUM",
                    label: getLabel("lotQuality_MEDIUM"),
                  },
                  {
                    value: "HIGH",
                    label: getLabel("lotQuality_HIGH"),
                  },
                ]}
                // defaultValue={{
                //   value: "HIGH",
                //   label: getLabel("lotQuality_HIGH"),
                // }}
                tooltip={getLabel("lotQualityTooltip")}
              />
              <Select
                name="product_line"
                control={control}
                label={getLabel("lotProductLine")}
                placeholder={getLabel("lotProductLinePlaceholder")}
                error={errors["product_line"]}
                options={[
                  {
                    value: "base",
                    label: getLabel("lotProductLine_base"),
                  },
                  {
                    value: "classic",
                    label: getLabel("lotProductLine_classic"),
                  },
                  {
                    value: "reserve",
                    label: getLabel("lotProductLine_reserve"),
                  },
                  {
                    value: "selection",
                    label: getLabel("lotProductLine_selection"),
                  },
                  {
                    value: "great_selection",
                    label: getLabel("lotProductLine_great_selection"),
                  },
                ]}
                tooltip={getLabel("lotProductLineTooltip")}
              />
              {(!id || isOnlyForm || isDuplicate) && (
                <Input
                  name="quantity"
                  register={register}
                  isDisabled={id && !isOnlyForm && !isDuplicate ? true : false}
                  type="number"
                  label={getLabel("lotQuantity")}
                  placeholder={getLabel("lotQuantityPlaceholder")}
                  error={errors["quantity"]}
                  required={getLabel("inputRequiredError")}
                  min={{
                    value: 1,
                    message: getLabel("errorMinValue", { value: 1 }),
                  }}
                  max={{
                    value: 50000,
                    message: getLabel("errorMaxValue", { value: 50000 }),
                  }}
                  regExpPattern={{
                    value: INT_REGEX,
                    message: getLabel("errorInt"),
                  }}
                  tooltip={getLabel("lotQuantityTooltip")}
                />
              )}
            </div>
            {(!id || isOnlyForm || isDuplicate) &&
              getValues("state")?.value === "WINE" && (
                <div className="lot_form_wrapper_row">
                  <Switch
                    leftOption={{
                      label: getLabel("lotType_FLOWER"),
                    }}
                    rightOption={{
                      label: getLabel("lotType_PRESSED"),
                    }}
                    toggleHandler={() => {
                      setIsPressed((prev) => !prev);
                    }}
                    status={isPressed}
                  />
                </div>
              )}
            <div className="lot_form_wrapper_row">
              {(!id || isOnlyForm) &&
                Number(getValues("quantity")) > 0 &&
                ["WINE", "MUST"].includes(getValues("state")?.value) && (
                  <NewLotCreationForm
                    lots={lots}
                    tanks={tanks}
                    activeWinery={activeWinery}
                    lot_id_in={{
                      ...getValues(),
                      batch_type: isPressed ? 0 : 1,
                    }}
                    // state={getValues("state")?.value}
                    // color={getValues("color")?.value}
                    register={register}
                    control={control}
                    handleCloseModal={handleCloseModal}
                    errors={errors}
                    getValues={getValues}
                    setValue={setValue}
                    clearErrors={clearErrors}
                    setError={setError}
                    varieties={varieties}
                    wineLabels={wineLabels}
                    trigger={trigger}
                    isOpen={isOpen}
                    isDuplicate={isDuplicate}
                  />
                )}
            </div>
            {(!id || isOnlyForm || isDuplicate) && (
              <>
                {/* Supplier and Vineyard Selection */}
                <div className="lot_form_wrapper_row">
                  <Select
                    name="supplier_id"
                    control={control}
                    label={getLabel("lotSupplier")}
                    placeholder={getLabel("lotSupplierPlaceholder")}
                    error={errors["supplier_id"]}
                    options={
                      suppliers?.map((s) => ({
                        value: s.id,
                        label: `${s.supplier_code} - ${s.company_name}`,
                      })) || []
                    }
                    tooltip={getLabel("lotSupplierTooltip")}
                  />

                  {supplier_id && filteredVineyards.length > 0 && (
                    <Select
                      name="vineyard_id"
                      control={control}
                      label={getLabel("lotVineyard")}
                      placeholder={getLabel("lotVineyardPlaceholder")}
                      error={errors["vineyard_id"]}
                      options={
                        filteredVineyards?.map((v) => ({
                          value: v.id,
                          label: `${v.vineyard_code} - ${v.name}`,
                        })) || []
                      }
                      tooltip={getLabel("lotVineyardTooltip")}
                    />
                  )}
                </div>

                <div className="lot_form_wrapper_row vertical">
                  {varietiesFields?.map?.((field, idx) => {
                    return (
                      <div key={field.id} className="lot_form_wrapper_row">
                        <Select
                          name={`varieties.${idx}`}
                          control={control}
                          label={getLabel("lotVarieties")}
                          placeholder={getLabel("lotVarietiesPlaceholder")}
                          error={errors["varieties"]}
                          required={getLabel("inputRequiredError")}
                          isSearchable={true}
                          isDisabled={!!lastAutoPopulatedVineyardRef.current}
                          options={getWineVarietiesOptionsByColor()?.map(
                            (v) => {
                              return {
                                value: v,
                                label: getLabel(`lotVarieties_${v}`),
                              };
                            },
                          )}
                          isLabel={idx === 0}
                          tooltip={
                            idx === 0 ? getLabel("lotVarietiesTooltip") : ""
                          }
                        />
                        {varietiesFields?.length > 1 &&
                          getValues(`varieties.${idx}`)?.value && (
                            <>
                              <Input
                                name={`varieties.${idx}.quantity`}
                                register={register}
                                type="number"
                                label={getLabel(`lotVarietiesPercentage`)}
                                placeholder={getLabel(
                                  "lotVarietiesPercentagePlaceholder",
                                )}
                                isLabel={idx === 0}
                                error={getError(idx, errors?.varieties)}
                                required={getLabel("inputRequiredError")}
                                disabled={
                                  !!lastAutoPopulatedVineyardRef.current
                                }
                                min={{
                                  value: 1,
                                  message: getLabel("errorMinValue", {
                                    value: 1,
                                  }),
                                }}
                                max={{
                                  value: 100,
                                  message: getLabel("errorMaxValue", {
                                    value: 100,
                                  }),
                                }}
                                regExpPattern={{
                                  value: INT_REGEX,
                                  message: getLabel("errorInt"),
                                }}
                                // onChange={updateErrors}
                              />
                            </>
                          )}
                        {varietiesFields?.length > 1 &&
                          !lastAutoPopulatedVineyardRef.current && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                removeV(idx);
                              }}
                            >
                              -
                            </button>
                          )}
                      </div>
                    );
                  })}
                  {!lastAutoPopulatedVineyardRef.current && (
                    <div className="lot_form_wrapper_row">
                      <p>{getLabel("lotVarietiesAdd")}</p>
                      <button
                        id="varietiesB"
                        onClick={(e) => {
                          e.preventDefault();
                          appendV({
                            label: "",
                            value: "",
                          });
                        }}
                      >
                        +
                      </button>
                    </div>
                  )}
                  {/* <Input
                  name="varieties"
                  register={register}
                  type="text"
                  label={getLabel("lotVarieties")}
                  placeholder={getLabel("lotVarietiesPlaceholder")}
                  error={errors["varieties"]}
                  regExpPattern={{
                    value: ALPHA_SPACE_APEX_DOT_REGEX,
                    message: getLabel("errorAlphaSpaceApex"),
                  }}
                  minLength={{
                    value: 3,
                    message: getLabel("errorMinLength", { value: 3 }),
                  }}
                  maxLength={{
                    value: 16,
                    message: getLabel("errorMaxLength", { value: 16 }),
                  }}
                  // tooltip={getLabel("lotVarietiesTooltip")}
                /> */}
                </div>
                <div className="lot_form_wrapper_row">
                  <Input
                    name="years"
                    register={register}
                    type="text"
                    label={getLabel("lotYears")}
                    placeholder={getLabel("lotYearsPlaceholder")}
                    error={errors["years"]}
                    required={getLabel("inputRequiredError")}
                    regExpPattern={{
                      value: YEAR_REGEX,
                      message: getLabel("errorYear"),
                    }}
                    minLength={{
                      value: 4,
                      message: getLabel("errorExactLength", { value: 4 }),
                    }}
                    maxLength={{
                      value: 4,
                      message: getLabel("errorExactLength", { value: 4 }),
                    }}
                    // tooltip={getLabel("lotYearsTooltip")}
                  />
                  <Input
                    name="origins"
                    register={register}
                    type="text"
                    label={getLabel("lotOrigins")}
                    required={getLabel("inputRequiredError")}
                    placeholder={getLabel("lotOriginsPlaceholder")}
                    error={errors["origins"]}
                    regExpPattern={{
                      value: NUM_ALPHA_SPACE_APEX_DOT_REGEX,
                      message: getLabel("errorAlphaSpaceApexNum"),
                    }}
                    minLength={{
                      value: 3,
                      message: getLabel("errorMinLength", { value: 3 }),
                    }}
                    maxLength={{
                      value: 32,
                      message: getLabel("errorMaxLength", { value: 32 }),
                    }}
                    // tooltip={getLabel("lotOriginsTooltip")}
                  />
                </div>

                {/* Sections specific to GRAPE state: Analysis, Health Status, Harvest Conditions */}
                {watchedState?.value === "GRAPE" && (
                  <>
                    {/* Analysis Protocols Section - Collapsible */}
                    <div className="lot_form_collapsible_section">
                      <div
                        className="lot_form_collapsible_header"
                        onClick={() => setIsAnalysisOpen(!isAnalysisOpen)}
                      >
                        <h5>{getLabel("lotAnalysisSection")}</h5>
                        <span
                          className={`collapse_icon ${isAnalysisOpen ? "open" : ""}`}
                        >
                          {isAnalysisOpen ? "▼" : "▶"}
                        </span>
                      </div>
                      {isAnalysisOpen && (
                        <div className="lot_form_collapsible_content">
                          <div className="lot_form_wrapper_row vertical">
                            <Select
                              name="field_analysis_protocol_id"
                              control={control}
                              label={getLabel("lotFieldAnalysisProtocol")}
                              placeholder={getLabel(
                                "lotFieldAnalysisProtocolPlaceholder",
                              )}
                              error={errors["field_analysis_protocol_id"]}
                              options={getFieldAnalysisOptions()}
                              tooltip={getLabel(
                                "lotFieldAnalysisProtocolTooltip",
                              )}
                            />

                            {/* Field Analysis Values */}
                            {selectedFieldProtocol?.analysis?.length > 0 && (
                              <div className="analysis_values_section">
                                <h6>{getLabel("lotFieldAnalysisValues")}</h6>
                                <div className="lot_form_wrapper_row vertical">
                                  {selectedFieldProtocol.analysis.map(
                                    (item, idx) => {
                                      const fieldValue = watch(
                                        `field_analysis_values.${idx}`,
                                      );
                                      let warningMsg = "";
                                      if (fieldValue) {
                                        const numValue = Number(fieldValue);
                                        if (
                                          item.min_range != null &&
                                          numValue < item.min_range
                                        ) {
                                          warningMsg = getLabel(
                                            "errorMinValue",
                                            { value: item.min_range },
                                          );
                                        } else if (
                                          item.max_range != null &&
                                          numValue > item.max_range
                                        ) {
                                          warningMsg = getLabel(
                                            "errorMaxValue",
                                            { value: item.max_range },
                                          );
                                        }
                                      }
                                      return (
                                        <Input
                                          key={`field-analysis-${idx}`}
                                          name={`field_analysis_values.${idx}`}
                                          register={register}
                                          type="number"
                                          isWarning={!!warningMsg}
                                          warningMessage={warningMsg}
                                          label={`${getLabel(`substance_${item.substance_name}`)} ${item?.unit != " " ? `(${item.unit})` : ""}`}
                                          placeholder={
                                            item.min_range != null ||
                                            item.max_range != null
                                              ? `${item.min_range || ""} - ${item.max_range || ""}`
                                              : ""
                                          }
                                          error={
                                            errors.field_analysis_values?.[idx]
                                          }
                                        />
                                      );
                                    },
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="lot_form_wrapper_row vertical reduce_gap">
                            <Select
                              name="lab_analysis_protocol_id"
                              control={control}
                              label={getLabel("lotLabAnalysisProtocol")}
                              placeholder={getLabel(
                                "lotLabAnalysisProtocolPlaceholder",
                              )}
                              error={errors["lab_analysis_protocol_id"]}
                              options={getLabAnalysisOptions()}
                              tooltip={getLabel(
                                "lotLabAnalysisProtocolTooltip",
                              )}
                            />
                            {watchedLabProtocolId?.value && <div className="lot_form_wrapper_row">
                              {/* <Input
                                name="lab_analysis_analyst"
                                register={register}
                                type="text"
                                label={getLabel("analysisAnalyst")}
                                placeholder={getLabel(
                                  "analysisAnalystPlaceholder",
                                )}
                                error={errors["lab_analysis_analyst"]}
                                required={getLabel("inputRequiredError")}
                                isReverse={true}
                                minLength={{
                                  value: 3,
                                  message: getLabel("errorMinLength", {
                                    value: 3,
                                  }),
                                }}
                                maxLength={{
                                  value: 32,
                                  message: getLabel("errorMaxLength", {
                                    value: 32,
                                  }),
                                }}
                                regExpPattern={{
                                  value: ALPHA_SPACE_APEX_DOT_REGEX,
                                  message: getLabel("errorAlphaSpaceApex"),
                                }}
                              /> */}
                              <Input
                                name="lab_analysis_custom_name"
                                register={register}
                                type="text"
                                label={getLabel("operationAnalysisName")}
                                placeholder={getLabel("operationAnalysisNamePlaceholder")}
                                minLength={{
                                  value: 3,
                                  message: getLabel("errorMinLength", { value: 3 }),
                                }}
                                maxLength={{
                                  value: 24,
                                  message: getLabel("errorMaxLength", { value: 24 }),
                                }}
                                regExpPattern={{
                                  value: ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
                                  message: getLabel("errorAlphaNumSpace_-"),
                                }}
                                error={errors["lab_analysis_custom_name"]}
                                tooltip={getLabel("operationAnalysisNameTooltip")}
                                validate={(value) => {
                                  const val = value?.trim() || null;
                                  if (!val) return;
                                  const analysisOps =
                                    operations?.filter(
                                      (o) => o?.type === "ANALYSIS" || o?.type === "TREATMENT"
                                    ) || [];
                                  const isNameAlreadyUsed =
                                    analysisOps?.some((o) => {
                                      return (
                                        o?.name?.toLowerCase() === val?.toLowerCase() &&
                                        o?.id !== id
                                      );
                                    }) || null;
                                  if (isNameAlreadyUsed)
                                    return getLabel("errorAnalysisNameUsed", { value: val });
                                }}
                              />
                              <MultipleSelect
                                name={`lab_analysis_cellarman`}
                                control={control}
                                label={getLabel("operationUser")}
                                placeholder={getLabel(
                                  "operationUserPlaceholder",
                                )}
                                error={errors["lab_analysis_cellarman"]}
                                required={getLabel("inputRequiredError")}
                                options={(user.role === 2 ? users?.filter((u) => u.username != user.userName && u.role_id === 3) : users)
                                  ?.map?.((user) => {
                                  return {
                                    value: user?.id,
                                    label: user?.username,
                                  };
                                })}
                                tooltip={getLabel("destemmingCellarmenTooltip")}
                              />
                              <Select
                                name="lab_analysis_priority"
                                control={control}
                                label={getLabel("operationPriority")}
                                placeholder={getLabel(
                                  "operationPriorityPlaceholder",
                                )}
                                error={errors}
                                // ["lab_analysis_priority"]}
                                required={getLabel("inputRequiredError")}
                                options={[
                                  {
                                    label: getLabel("lotQuality_HIGH"),
                                    value: "2",
                                  },
                                  {
                                    label: getLabel("lotQuality_MEDIUM"),
                                    value: "1",
                                  },
                                  {
                                    label: getLabel("lotQuality_LOW"),
                                    value: "0",
                                  },
                                ]}
                                // defaultValue={{
                                //     value: "",
                                //     label: "",
                                //   }}
                              />
                              <DatePicker
                                name="lab_analysis_expire_date"
                                control={control}
                                label={getLabel("operationExpireDateForm")}
                                error={errors["lab_analysis_expire_date"]}
                                required={getLabel("inputRequiredError")}
                                dateFormat={configs.shortDateFormat}
                                placeholder={getLabel(
                                  "expiringDatePlaceholder",
                                )}
                              />
                              {getValues("lab_analysis_expire_date") && (
                                <Input
                                  name={"lab_analysis_expire_date_exact"}
                                  register={register}
                                  type="checkbox"
                                  label={getLabel("isExactDate")}
                                  error={
                                    errors["lab_analysis_expire_date_exact"]
                                  }
                                  // isLabel={false}
                                  // customClassName={"confirm no_margin_top"}
                                  tooltip={getLabel("destemmingExactDate")}
                                />
                              )}
                            </div>}
                          </div>

                          {/* Lab Analysis Info - Analyses to be performed */}
                          {selectedLabProtocol?.analysis?.length > 0 && (
                            <div className="analysis_values_section">
                              <h6>
                                {getLabel("lotLabAnalysisInfo") ||
                                  "Analisi da effettuare"}
                              </h6>
                              <div className="lot_form_wrapper_row vertical">
                                <ul
                                  style={{
                                    paddingLeft: "20px",
                                    margin: "10px 0",
                                  }}
                                >
                                  {selectedLabProtocol.analysis.map(
                                    (item, idx) => (
                                      <li
                                        key={`lab-analysis-info-${idx}`}
                                        style={{ marginBottom: "5px" }}
                                      >
                                        <strong>
                                          {getLabel(
                                            `substance_${item.substance_name}`,
                                          )}
                                        </strong>
                                        {item?.unit &&
                                          item.unit !== " " &&
                                          ` (${item.unit})`}
                                        {(item.min_range != null ||
                                          item.max_range != null) && (
                                          <span
                                            style={{
                                              color: "#666",
                                              fontSize: "0.9em",
                                            }}
                                          >
                                            {" "}
                                            - {getLabel("substanceRang")}: {item.min_range ||
                                              ""} - {item.max_range || ""}
                                          </span>
                                        )}
                                      </li>
                                    ),
                                  )}
                                </ul>
                                <p
                                  style={{
                                    fontSize: "0.9em",
                                    color: "#666",
                                    fontStyle: "italic",
                                    margin: "10px 0",
                                  }}
                                >
                                  {getLabel("lotLabAnalysisAutoCreate") ||
                                    "Le analisi verranno create automaticamente dal sistema"}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Health Status Section - Collapsible */}
                    <div className="lot_form_collapsible_section">
                      <div
                        className="lot_form_collapsible_header"
                        onClick={() =>
                          setIsHealthStatusOpen(!isHealthStatusOpen)
                        }
                      >
                        <h5>{getLabel("lotHealthStatusSection")}</h5>
                        <span
                          className={`collapse_icon ${isHealthStatusOpen ? "open" : ""}`}
                        >
                          {isHealthStatusOpen ? "▼" : "▶"}
                        </span>
                      </div>
                      {isHealthStatusOpen && (
                        <div className="lot_form_collapsible_content">
                          <div className="lot_form_wrapper_row">
                            <Select
                              name="health_status.condition"
                              control={control}
                              label={getLabel("lotHealthCondition")}
                              placeholder={getLabel(
                                "lotHealthConditionPlaceholder",
                              )}
                              error={errors["health_status"]?.condition}
                              // required={getLabel("inputRequiredError")}
                              options={[
                                {
                                  value: "EXCELLENT",
                                  label: getLabel(
                                    "lotHealthCondition_EXCELLENT",
                                  ),
                                },
                                {
                                  value: "GOOD",
                                  label: getLabel("lotHealthCondition_GOOD"),
                                },
                                {
                                  value: "FAIR",
                                  label: getLabel("lotHealthCondition_FAIR"),
                                },
                                {
                                  value: "POOR",
                                  label: getLabel("lotHealthCondition_POOR"),
                                },
                              ]}
                              tooltip={getLabel("lotHealthConditionTooltip")}
                            />
                          </div>
                          <div className="lot_form_wrapper_row">
                            <Input
                              name="health_status.botrytis_percent"
                              register={register}
                              type="number"
                              label={getLabel("lotBotrytisPercent")}
                              placeholder="0-100"
                              error={errors["health_status"]?.botrytis_percent}
                              min={{
                                value: 0,
                                message: getLabel("errorMinValue", {
                                  value: 0,
                                }),
                              }}
                              max={{
                                value: 100,
                                message: getLabel("errorMaxValue", {
                                  value: 100,
                                }),
                              }}
                            />
                            <Input
                              name="health_status.rot_percent"
                              register={register}
                              type="number"
                              label={getLabel("lotRotPercent")}
                              placeholder="0-100"
                              error={errors["health_status"]?.rot_percent}
                              min={{
                                value: 0,
                                message: getLabel("errorMinValue", {
                                  value: 0,
                                }),
                              }}
                              max={{
                                value: 100,
                                message: getLabel("errorMaxValue", {
                                  value: 100,
                                }),
                              }}
                            />
                            <Input
                              name="health_status.damaged_berries_percent"
                              register={register}
                              type="number"
                              label={getLabel("lotDamagedBerriesPercent")}
                              placeholder="0-100"
                              error={
                                errors["health_status"]?.damaged_berries_percent
                              }
                              min={{
                                value: 0,
                                message: getLabel("errorMinValue", {
                                  value: 0,
                                }),
                              }}
                              max={{
                                value: 100,
                                message: getLabel("errorMaxValue", {
                                  value: 100,
                                }),
                              }}
                            />
                          </div>
                          <div className="lot_form_wrapper_row">
                            <Input
                              name="health_status.leaves_stems_percent"
                              register={register}
                              type="number"
                              label={getLabel("lotLeavesStemsPercent")}
                              placeholder="0-100"
                              error={
                                errors["health_status"]?.leaves_stems_percent
                              }
                              min={{
                                value: 0,
                                message: getLabel("errorMinValue", {
                                  value: 0,
                                }),
                              }}
                              max={{
                                value: 100,
                                message: getLabel("errorMaxValue", {
                                  value: 100,
                                }),
                              }}
                            />
                            <Input
                              name="health_status.immature_berries_percent"
                              register={register}
                              type="number"
                              label={getLabel("lotImmatureBerriesPercent")}
                              placeholder="0-100"
                              error={
                                errors["health_status"]
                                  ?.immature_berries_percent
                              }
                              min={{
                                value: 0,
                                message: getLabel("errorMinValue", {
                                  value: 0,
                                }),
                              }}
                              max={{
                                value: 100,
                                message: getLabel("errorMaxValue", {
                                  value: 100,
                                }),
                              }}
                            />
                          </div>
                          <div className="lot_form_wrapper_row">
                            <TextArea
                              name="health_status.notes"
                              register={register}
                              label={getLabel("lotHealthNotes")}
                              placeholder={getLabel(
                                "lotHealthNotesPlaceholder",
                              )}
                              error={errors["health_status"]?.notes}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Harvest Conditions Section - Collapsible */}
                    <div className="lot_form_collapsible_section">
                      <div
                        className="lot_form_collapsible_header"
                        onClick={() =>
                          setIsHarvestConditionsOpen(!isHarvestConditionsOpen)
                        }
                      >
                        <h5>{getLabel("lotHarvestConditionsSection")}</h5>
                        <span
                          className={`collapse_icon ${isHarvestConditionsOpen ? "open" : ""}`}
                        >
                          {isHarvestConditionsOpen ? "▼" : "▶"}
                        </span>
                      </div>
                      {isHarvestConditionsOpen && (
                        <div className="lot_form_collapsible_content">
                          <div className="lot_form_wrapper_row">
                            <DatePicker
                              name="harvest_conditions.harvest_date"
                              control={control}
                              label={getLabel("lotHarvestDate")}
                              error={errors["harvest_conditions"]?.harvest_date}
                              dateFormat={configs.shortDateFormat}
                            />
                          </div>
                          <div className="lot_form_wrapper_row">
                            <Select
                              name="harvest_conditions.method"
                              control={control}
                              label={getLabel("lotHarvestMethod")}
                              placeholder={getLabel(
                                "lotHarvestMethodPlaceholder",
                              )}
                              error={errors["harvest_conditions"]?.method}
                              options={[
                                {
                                  value: "manual",
                                  label: getLabel("lotHarvestMethod_manual"),
                                },
                                {
                                  value: "mechanical",
                                  label: getLabel(
                                    "lotHarvestMethod_mechanical",
                                  ),
                                },
                              ]}
                              tooltip={getLabel("lotHarvestMethodTooltip")}
                            />
                            <Input
                              name="harvest_conditions.container_type"
                              register={register}
                              type="text"
                              label={getLabel("lotContainerType")}
                              placeholder={getLabel(
                                "lotContainerTypePlaceholder",
                              )}
                              error={
                                errors["harvest_conditions"]?.container_type
                              }
                              maxLength={{
                                value: 50,
                                message: getLabel("errorMaxLength", {
                                  value: 50,
                                }),
                              }}
                            />
                          </div>

                          {/* Weather */}
                          <div className="lot_form_wrapper_row">
                            <h6>{getLabel("lotWeather")}</h6>
                          </div>
                          <div className="lot_form_wrapper_row">
                            <Input
                              name="harvest_conditions.weather.harvest_temperature"
                              register={register}
                              type="number"
                              label={getLabel("lotHarvestTemperature")}
                              placeholder="°C"
                              error={
                                errors["harvest_conditions"]?.weather
                                  ?.harvest_temperature
                              }
                            />
                            <Input
                              name="harvest_conditions.weather.humidity"
                              register={register}
                              type="number"
                              label={getLabel("lotHumidity")}
                              placeholder="%"
                              error={
                                errors["harvest_conditions"]?.weather?.humidity
                              }
                              min={{
                                value: 0,
                                message: getLabel("errorMinValue", {
                                  value: 0,
                                }),
                              }}
                              max={{
                                value: 100,
                                message: getLabel("errorMaxValue", {
                                  value: 100,
                                }),
                              }}
                            />
                            <Input
                              name="harvest_conditions.weather.conditions"
                              register={register}
                              type="text"
                              label={getLabel("lotWeatherConditions")}
                              placeholder={getLabel(
                                "lotWeatherConditionsPlaceholder",
                              )}
                              error={
                                errors["harvest_conditions"]?.weather
                                  ?.conditions
                              }
                              maxLength={{
                                value: 50,
                                message: getLabel("errorMaxLength", {
                                  value: 50,
                                }),
                              }}
                            />
                            <Input
                              name="harvest_conditions.weather.days_since_last_rain"
                              register={register}
                              type="number"
                              label={getLabel("lotDaysSinceLastRain")}
                              placeholder={getLabel(
                                "lotDaysSinceLastRainPlaceholder",
                              )}
                              error={
                                errors["harvest_conditions"]?.weather
                                  ?.days_since_last_rain
                              }
                              min={{
                                value: 0,
                                message: getLabel("errorMinValue", {
                                  value: 0,
                                }),
                              }}
                            />
                          </div>

                          {/* Transport */}
                          <div className="lot_form_wrapper_row">
                            <h6>{getLabel("lotTransport")}</h6>
                          </div>
                          <div className="lot_form_wrapper_row">
                            <Input
                              name="harvest_conditions.transport.time_from_harvest_hours"
                              register={register}
                              type="number"
                              label={getLabel("lotTimeFromHarvest")}
                              placeholder={getLabel(
                                "lotTimeFromHarvestPlaceholder",
                              )}
                              error={
                                errors["harvest_conditions"]?.transport
                                  ?.time_from_harvest_hours
                              }
                              min={{
                                value: 0,
                                message: getLabel("errorMinValue", {
                                  value: 0,
                                }),
                              }}
                            />
                            <Input
                              name="harvest_conditions.transport.transport_temperature"
                              register={register}
                              type="number"
                              label={getLabel("lotTransportTemperature")}
                              placeholder="°C"
                              error={
                                errors["harvest_conditions"]?.transport
                                  ?.transport_temperature
                              }
                            />
                          </div>
                          <div className="lot_form_wrapper_row">
                            <TextArea
                              name="harvest_conditions.transport.notes"
                              register={register}
                              label={getLabel("lotTransportNotes")}
                              placeholder={getLabel(
                                "lotTransportNotesPlaceholder",
                              )}
                              error={
                                errors["harvest_conditions"]?.transport?.notes
                              }
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Certifications Section - Collapsible (Always visible, not specific to GRAPE) */}
                <div className="lot_form_collapsible_section">
                  <div
                    className="lot_form_collapsible_header"
                    onClick={() =>
                      setIsCertificationsOpen(!isCertificationsOpen)
                    }
                  >
                    <h5>{getLabel("lotCertificationsSection")}</h5>
                    <span
                      className={`collapse_icon ${isCertificationsOpen ? "open" : ""}`}
                    >
                      {isCertificationsOpen ? "▼" : "▶"}
                    </span>
                  </div>
                  {isCertificationsOpen && (
                    <div className="lot_form_collapsible_content">
                      {/* Note: Organic syncs with "Bio", Vegan syncs with "Vegan", Biodynamic syncs with "Biodynamic" labels */}
                      <div className="lot_form_wrapper_row">
                        <Input
                          name="certifications.organic"
                          register={register}
                          type="checkbox"
                          label={getLabel("lotCertificationOrganic")}
                          error={errors["certifications"]?.organic}
                        />
                        <Input
                          name="certifications.vegan"
                          register={register}
                          type="checkbox"
                          label={getLabel("lotCertificationVegan")}
                          error={errors["certifications"]?.vegan}
                        />
                        <Input
                          name="certifications.biodynamic"
                          register={register}
                          type="checkbox"
                          label={getLabel("lotCertificationBiodynamic")}
                          error={errors["certifications"]?.biodynamic}
                        />
                      </div>

                      {/* ISO Traceability */}
                      <div className="lot_form_wrapper_row">
                        <h6>{getLabel("lotIsoTraceability")}</h6>
                      </div>
                      <div className="lot_form_wrapper_row">
                        <Input
                          name="certifications.iso_traceability.field_lot"
                          register={register}
                          type="text"
                          label={getLabel("lotIsoFieldLot")}
                          placeholder={getLabel("lotIsoFieldLotPlaceholder")}
                          error={
                            errors["certifications"]?.iso_traceability
                              ?.field_lot
                          }
                          maxLength={{
                            value: 50,
                            message: getLabel("errorMaxLength", { value: 50 }),
                          }}
                        />
                        <Input
                          name="certifications.iso_traceability.vineyard_registry"
                          register={register}
                          type="text"
                          label={getLabel("lotIsoVineyardRegistry")}
                          placeholder={getLabel(
                            "lotIsoVineyardRegistryPlaceholder",
                          )}
                          error={
                            errors["certifications"]?.iso_traceability
                              ?.vineyard_registry
                          }
                          maxLength={{
                            value: 50,
                            message: getLabel("errorMaxLength", { value: 50 }),
                          }}
                        />
                        <Input
                          name="certifications.iso_traceability.phytosanitary_treatments"
                          register={register}
                          type="text"
                          label={getLabel("lotIsoPhytosanitaryTreatments")}
                          placeholder={getLabel(
                            "lotIsoPhytosanitaryTreatmentsPlaceholder",
                          )}
                          error={
                            errors["certifications"]?.iso_traceability
                              ?.phytosanitary_treatments
                          }
                          maxLength={{
                            value: 200,
                            message: getLabel("errorMaxLength", { value: 200 }),
                          }}
                        />
                      </div>

                      {/* Documents */}
                      <div className="lot_form_wrapper_row">
                        <h6>{getLabel("lotCertificationDocuments")}</h6>
                      </div>
                      {documentsFields?.map((field, idx) => {
                        return (
                          <div key={field.id} className="lot_form_wrapper_row">
                            <Input
                              name={`certifications.documents.${idx}.type`}
                              register={register}
                              type="text"
                              label={getLabel("lotDocumentType")}
                              placeholder={getLabel(
                                "lotDocumentTypePlaceholder",
                              )}
                              error={
                                errors["certifications"]?.documents?.[idx]?.type
                              }
                              required={getLabel("inputRequiredError")}
                              isLabel={idx === 0}
                            />
                            <Input
                              name={`certifications.documents.${idx}.number`}
                              register={register}
                              type="text"
                              label={getLabel("lotDocumentNumber")}
                              placeholder={getLabel(
                                "lotDocumentNumberPlaceholder",
                              )}
                              error={
                                errors["certifications"]?.documents?.[idx]
                                  ?.number
                              }
                              required={getLabel("inputRequiredError")}
                              isLabel={idx === 0}
                            />
                            <DatePicker
                              name={`certifications.documents.${idx}.date`}
                              control={control}
                              label={getLabel("lotDocumentDate")}
                              error={
                                errors["certifications"]?.documents?.[idx]?.date
                              }
                              isLabel={idx === 0}
                              dateFormat={configs.shortDateFormat}
                            />

                            <Input
                              name={`certifications.documents.${idx}.issuing_authority`}
                              register={register}
                              type="text"
                              label={getLabel("lotDocumentIssuingAuthority")}
                              placeholder={getLabel(
                                "lotDocumentIssuingAuthorityPlaceholder",
                              )}
                              error={
                                errors["certifications"]?.documents?.[idx]
                                  ?.issuing_authority
                              }
                              isLabel={idx === 0}
                            />
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                removeDoc(idx);
                              }}
                            >
                              -
                            </button>
                          </div>
                        );
                      })}
                      <div className="lot_form_wrapper_row">
                        <p>{getLabel("lotAddDocument")}</p>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            appendDoc({
                              type: "",
                              number: "",
                              date: "",
                              issuing_authority: "",
                            });
                          }}
                        >
                          +
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {id && !isDuplicate && !isOnlyForm && (
              <div className="lot_form_wrapper">
                <div className="lot_form_wrapper_row">
                  <Label label={getLabel("lotVarieties")}>
                    {lot?.composition?.map((c, index) => (
                      <p key={`varieties-${index}`}>
                        {c?.variety
                          ? `${getLabel(`lotVarieties_${c.variety}`)}${lot?.composition?.length > 1 ? ` (${c.percentage}%)` : ""}`
                          : ""}
                      </p>
                    )) || ""}
                  </Label>

                  <Label label={getLabel("lotYears")}>
                    {lot?.composition?.map((c, index) => (
                      <p key={`years-${index}`}>{c?.year || ""}</p>
                    )) || ""}
                  </Label>

                  <Label label={getLabel("lotOrigins")}>
                    {lot?.composition?.map((c, index) => (
                      <p key={`origins-${index}`}>{c?.origin || ""}</p>
                    )) || ""}
                  </Label>
                </div>
                <div className="lot_form_wrapper_row">
                  <Label
                    label={getLabel("lotColor")}
                    // tooltip={getLabel("lotColorTooltip")}
                  >
                    {getLabel(`lotColor_${lot?.color || "RED"}`)}
                  </Label>
                  <Label
                    label={getLabel("lotState")}
                    // tooltip={getLabel("lotColorTooltip")}
                  >
                    {getLabel(`lotState_${lot?.state || ""}`)}
                  </Label>
                  <Label
                    label={getLabel("lotQuantity")}
                    // tooltip={getLabel("lotColorTooltip")}
                  >
                    {lot?.actual_quantity || 0} L
                  </Label>
                  {lot?.product_line && (
                    <Label label={getLabel("lotProductLine")}>
                      {getLabel(`lotProductLine_${lot?.product_line}`)}
                    </Label>
                  )}
                </div>

                {/* {lot?.varieties?.map((l, index) => {
                  return (
                    <Select
                      name={`varieties.${index}`}
                      isLabel={index === 0}
                      control={control}
                      label={getLabel("lotVarieties")}
                      placeholder={getLabel("lotVarietiesPlaceholder")}
                      error={errors["varieties"]}
                      required={getLabel("inputRequiredError")}
                      options={getWineVarietiesOptionsByColor()?.map((v) => {
                        return { value: v, label: getLabel(`lotVarieties_${v}`) };
                      })}
                      tooltip={getLabel("lotVarietiesTooltip")}
                    />
                    // <Input
                    //   name={`varieties.${index}`}
                    //   register={register}
                    //   type="text"
                    //   label={getLabel("lotVarieties")}
                    //   isLabel={index === 0}
                    //   placeholder={getLabel("lotVarietiesPlaceholder")}
                    //   error={errors[`varieties`]?.[index]}
                    //   minLength={{
                    //     value: 3,
                    //     message: getLabel("errorMinLength", { value: 3 }),
                    //   }}
                    //   maxLength={{
                    //     value: 16,
                    //     message: getLabel("errorMaxLength", { value: 16 }),
                    //   }}
                    //   regExpPattern={{
                    //     value: ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
                    //     message: getLabel("errorAlphaNumSpace_-"),
                    //   }}
                    //   //   tooltip={index === 0 ? getLabel("lotVarietiesTooltip") : ''}
                    // />
                  );
                })}
                {lot?.years?.map((l, index) => {
                  return (
                    <Input
                      name={`years.${index}`}
                      register={register}
                      type="number"
                      isLabel={index === 0}
                      label={getLabel("lotYears")}
                      placeholder={getLabel("lotYearsPlaceholder")}
                      error={errors[`years`]?.[index]}
                      minLength={{
                        value: 4,
                        message: getLabel("errorExactLength", { value: 4 }),
                      }}
                      maxLength={{
                        value: 4,
                        message: getLabel("errorExactLength", { value: 14 }),
                      }}
                      regExpPattern={{
                        value: YEAR_REGEX,
                        message: getLabel("errorYear"),
                      }}
                      //   tooltip={index === 0 ? getLabel("lotYearsTooltip") : ''}
                    />
                  );
                })}
                {lot?.origins?.map((l, index) => {
                  return (
                    <Input
                      name={`origins.${index}`}
                      register={register}
                      type="text"
                      isLabel={index === 0}
                      label={getLabel("lotOrigins")}
                      placeholder={getLabel("lotOriginsPlaceholder")}
                      error={errors[`origins`]?.[index]}
                      minLength={{
                        value: 3,
                        message: getLabel("errorMinLength", { value: 3 }),
                      }}
                      maxLength={{
                        value: 32,
                        message: getLabel("errorMaxLength", { value: 32 }),
                      }}
                      regExpPattern={{
                        value: ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
                        message: getLabel("errorAlphaNumSpace_-"),
                      }}
                      //   tooltip={index === 0 ? getLabel("lotOriginsTooltip") : ''}
                    />
                  );
                })} */}
              </div>
            )}
            {/* DOCO Section - Collapsible */}
            <div className="lot_form_collapsible_section">
              <div
                className="lot_form_collapsible_header"
                onClick={() => setIsDocoOpen(!isDocoOpen)}
              >
                <h5>{getLabel("lotDocoSection")}</h5>
                <span className={`collapse_icon ${isDocoOpen ? "open" : ""}`}>
                  {isDocoOpen ? "▼" : "▶"}
                </span>
              </div>
              {isDocoOpen && (
                <div className="lot_form_collapsible_content">
                  <div className="lot_form_wrapper_row">
                    <Input
                      name={"doco_id"}
                      label={getLabel("lotDoco")}
                      placeholder={getLabel("lotDocoPlaceholder")}
                      tooltip={getLabel("lotDocoTooltip")}
                      register={register}
                      error={errors["doco_id"]}
                      // required={getLabel("inputRequiredError")}
                      type="text"
                      minLength={{
                        value: 3,
                        message: getLabel("errorMinLength", { value: 3 }),
                      }}
                      maxLength={{
                        value: 24,
                        message: getLabel("errorMaxLength", { value: 24 }),
                      }}
                      regExpPattern={{
                        value: ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
                        message: getLabel("errorAlphaNumSpace_-"),
                      }}
                    />
                    <DatePicker
                      name="doco_date"
                      control={control}
                      label={getLabel("lotDocoDate")}
                      error={errors["doco_date"]}
                      dateFormat={configs.shortDateFormat}
                    />
                  </div>
                </div>
              )}
            </div>

            {false && id && !isOnlyForm && !isDuplicate && (
              <div>
                {lot.state !== "GRAPE" && (
                  <>
                    <h4>{getLabel("lotTanks")}</h4>
                    <div className="lot_form_wrapper_row">
                      <div className="tanks">
                        {tankList?.map((tank, index) => {
                          return (
                            <div className="tank" key={`tank-detail-${index}`}>
                              {/* <RecapTankIcon
                                tank={tank}
                                wineColor={getWineColor(lot?.color)}
                              /> */}
                              <TankIcon tank={tank} />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
            <div className="lot_form_wrapper_row">
              <TextArea
                name="note"
                register={register}
                label={getLabel("lotNotes")}
                placeholder={getLabel("lotNotesPlaceholder")}
                error={errors["note"]}
                tooltip={getLabel("lotNotesTooltip")}
              />
            </div>
            <div className="lot_form_wrapper_row">
              <Submit label={getLabel("submitForm")} />
            </div>
          </form>
        </div>
        {/* {lotLabelOpen?.value !== "Vino da tavola" && (
          <div className={`lot_label_container ${lotLabelOpen ? "open" : ""}`}>
            <div className="lot_form_wrapper_row">
              <div>
                <h3>{lotLabelOpen?.value}</h3>
                <div className="label_wrapper">{renderLotLabelData()}</div>
              </div>
            </div>
            <div className="lot_form_wrapper_row">
              <div className="bio-vegan_wrapper">
                {label?.find((l) => l?.label === "Bio") && (
                  <img className="icon" src={bio} alt={"bio"} />
                )}
                {label?.find((l) => l?.label === "Vegan") && (
                  <img className="icon" src={vegan} alt={"vegan"} />
                )}
              </div>
            </div>
          </div>
        )} */}
      </div>
    );
  };

  const renderLotLabelData = () => {
    const wineLabel =
      wineLabels?.wine?.find((w) => w?.id === lotLabelOpen?.value) || null;
    if (wineLabel == null) return;

    const limits = wineLabel?.limits || [];
    const refinements = wineLabel?.refinements || [];

    return (
      <div>
        <h4 className="label_title">{getLabel("disciplinary")}</h4>
        <div>
          <h5 className="label_subtitle">{getLabel("wineComposition")}</h5>
          {limits?.map((l) => {
            return (
              <div className="list-item">
                {l?.type === "RANGE" && (
                  <p>
                    <span>{l?.wines?.[0] || ""}</span>
                    {` ${l?.min * 100 || 0} - ${l?.max * 100 || 100}%`}
                  </p>
                )}
                {l?.type === "COLOR" && (
                  <p>
                    <span>{getLabel(`lotColor_${l?.color}`) || ""}</span>
                    {` ${l?.min * 100 || 0} - ${l?.max * 100 || 100}%`}
                  </p>
                )}
                {l?.type === "OTHER_COLOR" && (
                  <p>
                    <span>{getLabel("otherVarieties")}</span>{" "}
                    {getLabel(`lotColor_${l?.color}`) || ""}{" "}
                    {` ${l?.min * 100 || 0} - ${l?.max * 100 || 100}%`}
                  </p>
                )}
                {l?.type === "SUM" && (
                  <p>
                    <span>{l?.wines?.[0] || ""}</span>
                    {` ${l?.min * 100 || 0} - ${l?.max * 100 || 100}%`}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        <div>
          <h5 className="label_subtitle">{getLabel("refinement")}</h5>
          {refinements?.map((r) => {
            return (
              <div className="list-item">
                {r?.id && (
                  <p>
                    <span>{r?.id || ""}</span>
                    {` ${r?.min || ""} - ${r?.max || "..."} ${getLabel("refinementMonths")}`}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <>
      {!isOnlyForm && (
        <div className="primary_container">
          <div className="primary_container_top">
            <SecondaryBar
              breadCrumb={[
                { label: getLabel("lotsNavLink"), link: "/lots" },
                getLabel(
                  id && !isDuplicate
                    ? "lotsUpdatePage"
                    : isDuplicate
                      ? "lotsCopyPage"
                      : "lotsNewPage",
                ),
              ]}
            />
            {renderNavMenu()}
          </div>
          <div className="primary_container_bottom">{renderForm()}</div>
        </div>
      )}
      {isOnlyForm && renderForm()}
    </>
  );
};

export default LotForm;
